import { useApi } from '@hooks/useApi';
import { useConfirmedCallback } from '@hooks/useConfirmedCallback';
import { Badge, Blockquote, Button } from '@ui';
import { BadgeProps } from '@ui/Badge';
import { cx } from 'class-variance-authority';
import { useClubSlug } from 'components/Clubs/hooks';
import Loader from 'components/Loader';
import { PaymentMethodDetails } from 'components/Mollie/PaymentMethodDetails';
import { PaymentMethodIcon } from 'components/Mollie/PaymentMethodIcon';
import moment from 'moment';
import { Key, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentModuleKey } from '../types';
import useMandates, { mandateQuery } from './useMandates';

type MandateStatus = 'valid' | 'pending' | 'invalid';

interface MandateSelectionProps {
    onSelectMandate: (mandateId?: Key | null) => void;
    selectedMandateId?: string;
    createMandate?: boolean;
    module?: PaymentModuleKey;
}

export default function MandateSelection({
    onSelectMandate,
    selectedMandateId,
    createMandate,
    module
}: MandateSelectionProps) {
    const { t } = useTranslation('general');
    const api = useApi();
    const clubSlug = useClubSlug();
    const { data, isLoading, mutate } = useMandates(clubSlug, undefined, module);
    const [isLoadingRevoke, setIsLoadingRevoke] = useState<string[]>([]);

    const handleRevokeMandate = useConfirmedCallback(
        (mandateId: string) => {
            setIsLoadingRevoke((prev) => [...prev, mandateId]);
            onSelectMandate(undefined);
            api({
                url: `connect/${clubSlug}/mandate/${mandateId}${mandateQuery(module)}`,
                method: 'DELETE',
                onBefore: () => setIsLoadingRevoke((prev) => prev.filter((id) => id !== mandateId)),
                onSuccess: () => mutate(data.filter((item: any) => item.id !== mandateId))
            });
        },
        {
            message: t('mollie.mandateSelection.revokeConfirm')
        }
    );

    if (isLoading) return null;

    if (!isLoading && data.length === 0) return <Blockquote>{t('mollie.mandateSelection.emptyState')}</Blockquote>;

    return (
        <Loader isLoading={isLoading}>
            <div className="tw-grid tw-grid-cols-[minmax(min-content,1fr)] tw-gap-x-4">
                {data?.map((item: any) => (
                    <div key={item.id} className="tw-pb-4 tw-pr-4 tw-pt-4">
                        <div
                            onClick={() => {
                                if (item.status !== 'valid') return;
                                if (selectedMandateId === item.id) return onSelectMandate(undefined);
                                else onSelectMandate(item.id);
                            }}
                            className={cx(
                                'tw-card-bordered tw-card-clickable tw-relative',
                                selectedMandateId === item.id && 'tw-border-success'
                            )}
                        >
                            <MandateStatus className="tw-absolute -tw-right-4 -tw-top-4" status={item.status} />
                            <div className="tw-mb-2 tw-flex tw-items-center tw-gap-x-4 tw-border-0 tw-border-b tw-border-solid tw-border-b-accents-2 tw-pb-2">
                                <PaymentMethodIcon method={item.method} />
                                <div className="tw-grid tw-flex-1 tw-grid-cols-2 tw-gap-x-2 tw-text-xs">
                                    <span className="tw-font-semibold">{t('mollie.mandateSelection.signedAt')}</span>
                                    <span>{moment(item.signedAt).format('lll')}</span>
                                    <span className="tw-font-semibold">{t('mollie.mandateSelection.createdAt')}</span>
                                    <span>{moment(item.createdAt).format('lll')}</span>
                                </div>
                            </div>
                            <PaymentMethodDetails className="tw-text-accents-7" details={item.details} />
                            <Button
                                isLoading={isLoadingRevoke.includes(item.id)}
                                className="tw-mt-2"
                                onPress={() => handleRevokeMandate(item.id)}
                                block
                                color="light"
                                size="xs"
                            >
                                {t('mollie.mandateSelection.revokeButton')}
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </Loader>
    );
}

function MandateStatus({ status, ...props }: { status: MandateStatus } & BadgeProps) {
    const { t } = useTranslation('general');

    const renderMandateStatusText = (status: MandateStatus) => {
        switch (status) {
            case 'valid':
                return t('mollie.mandateSelection.status.valid');
            case 'pending':
                return t('mollie.mandateSelection.status.pending');
            case 'invalid':
                return t('mollie.mandateSelection.status.invalid');
            default:
                return t('mollie.mandateSelection.status.unknown');
        }
    };

    const renderMandateStatusColor = (status: MandateStatus) => {
        switch (status) {
            case 'valid':
                return 'success';
            case 'pending':
                return 'warning';
            case 'invalid':
                return 'error';
            default:
                return 'light';
        }
    };

    return (
        <Badge {...props} color={renderMandateStatusColor(status)}>
            {renderMandateStatusText(status)}
        </Badge>
    );
}
